export const DEFAULT_REG_FILTERS = {
  search: '', status: '', paymentStatus: '', waiver: '', guestWaiver: '',
  session: '', item: '', day: '', passcode: '', leadSent: '', pageIndex: 0,
};
export const DEFAULT_SORT_ORDER = 'createdAt|desc';
export const normalizeSortOrder = (value) => typeof value === 'string' &&
  /^(createdAt\|desc|(firstName|lastName|email)\|(asc|desc)|sessionDate\|sessionTime\|(asc|desc))$/.test(value) ? value : DEFAULT_SORT_ORDER;
export const DEFAULT_FIELDS = {
  company: false, title: false, firstName: true, lastName: true, email: true,
  phone: false, address1: false, address2: false, city: false, state: false,
  postal: false, createdDate: true, updatedDate: false, status: true,
  paymentStatus: false, guest: false, guestEmail: false, waiver: false,
  guestWaiver: false, dealer_optin: false, dealer: false, experience: false,
  modelInterest: false, purchaseTimeFrame: false, disableLead: false,
  leadSent: false, passcode: false, itemInfo: false, sessionInfo: false, notes: false,
};
export const BASE_AVAILABLE_FIELDS = Object.keys(DEFAULT_FIELDS);
export const getRegistrationsTableStorageKey = (eventId, key) => `registrationsTable:${eventId || 'global'}:${key}`;
export const readRegistrationsTableSetting = (eventId, key, fallback) => {
  try {
    const value = JSON.parse(localStorage.getItem(getRegistrationsTableStorageKey(eventId, key)));
    return value ?? fallback;
  } catch { return fallback; }
};
export const writeRegistrationsTableSetting = (eventId, key, value) => {
  try { localStorage.setItem(getRegistrationsTableStorageKey(eventId, key), JSON.stringify(value)); return true; }
  catch { return false; }
};
export const normalizeRegFilters = (filters) => {
  const normalized = { ...DEFAULT_REG_FILTERS };
  Object.keys(normalized).forEach((key) => {
    if (key !== 'pageIndex' && typeof filters?.[key] === 'string') normalized[key] = filters[key];
  });
  normalized.pageIndex = Number.isSafeInteger(filters?.pageIndex) ? Math.max(0, filters.pageIndex) : 0;
  return normalized;
};
export const registrationMatchesStatusFilter = (reg, status) => {
  if (!status) return true;
  if (status === 'Not Confirmed') return reg.status === 'Registered' && !reg.isConfirmed;
  if (status === 'Confirmed') return reg.status === 'Registered' && !!reg.isConfirmed;
  if (status === 'No Show-Cancelled') return reg.status === 'Cancelled' && !!reg.isNoShow;
  if (status === 'Self-Cancelled') return reg.status === 'Cancelled' && !!reg.isConfirmed && !reg.isNoShow;
  return reg.status === status;
};
export const filterRegistrations = (registrations, filters) => {
  const f = normalizeRegFilters(filters);
  const search = f.search.trim().toLowerCase();
  return registrations.filter((reg) =>
    (!search || [reg.company, `${reg.firstName || ''} ${reg.lastName || ''}`, reg.email, reg.id, reg.phone]
      .some((value) => String(value || '').toLowerCase().includes(search))) &&
    registrationMatchesStatusFilter(reg, f.status) &&
    (!f.paymentStatus || reg.adyenPaymentStatus === f.paymentStatus) &&
    (!f.waiver || !!reg.hasWaiver === (f.waiver === 'Yes')) &&
    (!f.guestWaiver || (f.guestWaiver === 'Yes' ? !!reg.hasGuestWaiver :
      !reg.hasGuestWaiver && !!(reg.guestFirstName || reg.guestLastName))) &&
    (!f.session || `${reg.sessionDate} ${reg.sessionTime}` === f.session) &&
    (!f.day || reg.sessionDate === f.day) &&
    (!f.item || String(reg.sessionItemId) === f.item) &&
    (!f.passcode || String(reg.passcode || '').toLowerCase() === f.passcode.toLowerCase()) &&
    (!f.leadSent || !!reg.leadSent === (f.leadSent === 'Yes')));
};
const timestamp = (value) => value?.seconds ? value.seconds * 1000 : new Date(value || 0).getTime();
export const sortRegistrations = (registrations, sortOrder = DEFAULT_SORT_ORDER, sessionTimestamp = () => 0) => {
  const [field, directionOrTime, sessionDirection] = normalizeSortOrder(sortOrder).split('|');
  const direction = sessionDirection || directionOrTime;
  return [...registrations].sort((a, b) => {
    const left = sessionDirection ? sessionTimestamp(a) : field === 'createdAt' ? timestamp(a[field]) : String(a[field] || '');
    const right = sessionDirection ? sessionTimestamp(b) : field === 'createdAt' ? timestamp(b[field]) : String(b[field] || '');
    const difference = typeof left === 'number' ? (left || 0) - (right || 0) : left.localeCompare(right, undefined, { sensitivity: 'base' });
    return (direction === 'desc' ? -difference : difference) || String(a.id).localeCompare(String(b.id));
  });
};
export const clampPageIndex = (pageIndex, count, pageSize) => Math.min(Math.max(0, pageIndex), Math.max(0, Math.ceil(count / pageSize) - 1));
export const fieldLabel = (key, fields = []) => fields.find((field) => field.fieldName === key)?.fieldLabel?.replace(/<[^>]*>/g, '') ||
  ({ dealer_optin: 'Dealer opt-in', leadSent: 'Sent to C@P', itemInfo: 'Item', sessionInfo: 'Session', adyenRef: 'Payment reference',
    adyenLink: 'Payment link', notes: 'Legacy notes', subCost: 'Subtotal', taxCost: 'Tax', totalCost: 'Total', createdDate: 'Created',
    updatedDate: 'Updated', disableLead: 'Lead disabled' }[key] || key.replace(/([A-Z])/g, ' $1').replace(/([a-z])(\d)/g, '$1 $2').replace(/^./, (character) => character.toUpperCase()));
